/**
 * useProjectWorkSessions Hook (AGENT-STATUS-1)
 *
 * The open work sessions of one project, kept live: refetched when a push over the bridge
 * socket touches this project (an agent:status whose projectId or agent.activeSessions name
 * it, or any push about an agent already listed here, whose run may just have left it),
 * debounced so a burst of reports is one fetch; and every minute while the tab is visible,
 * so a session that has gone quiet shows "No update since ...".
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import type { Socket } from 'socket.io-client';
import type { WorkSession } from '../types/agent';
import { getWorkSessions } from '../services/agentService';

const REFRESH_INTERVAL_MS = 60000;
const PUSH_DEBOUNCE_MS = 300;
const SESSION_EVENTS = ['agent-session:started', 'agent-session:ended'] as const;

interface StatusPush {
  agentId?: string;
  projectId?: string | null;
  agent?: { activeSessions?: Array<{ projectId: string | null }> };
}
interface SessionPush {
  agentId?: string;
  projectId?: string | null;
}

interface UseProjectWorkSessionsResult {
  sessions: WorkSession[];
  isLoading: boolean;
  error: string | null;
}

export function useProjectWorkSessions(projectId: string, socket?: Socket | null): UseProjectWorkSessionsResult {
  const [sessions, setSessions] = useState<WorkSession[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const latestRequest = useRef(0);

  const load = useCallback(async () => {
    const request = ++latestRequest.current;
    try {
      const data = await getWorkSessions(projectId);
      if (request !== latestRequest.current) return; // a newer fetch (or another project) won
      setSessions(data);
      setError(null);
    } catch (err) {
      if (request !== latestRequest.current) return;
      setError(err instanceof Error ? err.message : 'Could not load the agents on this project');
      console.error('[useProjectWorkSessions] Fetch failed:', err);
    } finally {
      if (request === latestRequest.current) setIsLoading(false);
    }
  }, [projectId]);

  // The agents listed now: a push about one of them may be its run leaving this project.
  const listedAgents = useRef<Set<string>>(new Set());
  useEffect(() => {
    listedAgents.current = new Set(sessions.map((s) => s.agentId));
  }, [sessions]);

  useEffect(() => {
    setIsLoading(true);
    setSessions([]);
    void load();
    let interval: ReturnType<typeof setInterval> | null = null;
    const startTimer = (): void => {
      if (!interval) interval = setInterval(() => void load(), REFRESH_INTERVAL_MS);
    };
    const stopTimer = (): void => {
      if (interval) clearInterval(interval);
      interval = null;
    };
    const onVisibility = (): void => {
      if (document.hidden) return stopTimer();
      void load(); // catch up on what changed while hidden
      startTimer();
    };
    if (!document.hidden) startTimer();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stopTimer();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [load]);

  useEffect(() => {
    if (!socket) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const refresh = (): void => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => void load(), PUSH_DEBOUNCE_MS);
    };
    const isListed = (agentId: string | undefined): boolean => !!agentId && listedAgents.current.has(agentId);
    const onStatus = (...args: unknown[]): void => {
      const push = (args[0] ?? {}) as StatusPush;
      const touches = push.projectId === projectId
        || (push.agent?.activeSessions ?? []).some((s) => s.projectId === projectId)
        || isListed(push.agentId);
      if (touches) refresh();
    };
    const onSession = (...args: unknown[]): void => {
      const push = (args[0] ?? {}) as SessionPush;
      if (push.projectId === projectId || isListed(push.agentId)) refresh();
    };
    socket.on('agent:status', onStatus);
    for (const event of SESSION_EVENTS) socket.on(event, onSession);
    return () => {
      if (timer) clearTimeout(timer);
      socket.off('agent:status', onStatus);
      for (const event of SESSION_EVENTS) socket.off(event, onSession);
    };
  }, [socket, load, projectId]);

  return { sessions, isLoading, error };
}
